import React, { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import type {
  GetCourseStudentInsightsQuery,
  GetUnifiedSubmissionsByFacultyByUserQueryVariables,
} from "@axio/graphql/generated/graphql";
import { useGetUnifiedSubmissionsByFacultyByUserQuery } from "@axio/graphql/generated/graphql";
import type { RootStackParamList } from "../../../../navigation/navigation.types";
import { colors, radii, spacing } from "../../../../styles/design-system";
import { isSubmissionLate } from "../../../../utils/date";

import RosterInsightGradeBreakdownCard from "./components/RosterInsightGradeBreakdownCard";
import RosterInsightSubmissionsTable from "./components/RosterInsightSubmissionsTable";
import RosterInsightSummaryMetrics from "./components/RosterInsightSummaryMetrics";
import {
  COURSE_SUBMISSIONS_FETCH_LIMIT,
  getBreakdownProgressColor,
  getRoundedPercentage,
  getSubmissionSortTimestamp,
  getSubmissionStatus,
  groupSubmissionsByAttemptKey,
  sumSubmissionPoints,
  type GradeBreakdownRow,
  type SubmissionTableRow,
} from "./RosterInsightGradesAndSubmissions.utils";

interface RosterInsightGradesAndSubmissionsContainerProps {
  studentInsights: NonNullable<GetCourseStudentInsightsQuery["getCourseStudentInsights"]["data"]> | null;
}

const RosterInsightGradesAndSubmissionsContainer = ({
  studentInsights,
}: RosterInsightGradesAndSubmissionsContainerProps) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const studentId = studentInsights?.studentId ?? "";
  const courseId = studentInsights?.courseId ?? "";

  const submissionQueryInput: GetUnifiedSubmissionsByFacultyByUserQueryVariables["input"] = {
    userId: studentId,
    courseId,
    submissionType: "All",
    page: 1,
    pageSize: COURSE_SUBMISSIONS_FETCH_LIMIT,
  };

  const { data: submissionsData } = useGetUnifiedSubmissionsByFacultyByUserQuery({
    variables: {
      input: submissionQueryInput,
    },
    skip: !studentId || !courseId,
    fetchPolicy: "network-only",
  });

  const fetchedSubmissions = useMemo(
    () => submissionsData?.getUnifiedSubmissionsByFacultyByUser?.data?.submissions ?? [],
    [submissionsData?.getUnifiedSubmissionsByFacultyByUser?.data?.submissions]
  );

  const courseSubmissions = useMemo(
    () =>
      fetchedSubmissions.flatMap((submission) => {
        if (!submission || (submission.submissionType !== "Assignment" && submission.submissionType !== "Assessment")) {
          return [];
        }

        return [submission];
      }),
    [fetchedSubmissions]
  );

  const submissionRows = useMemo<SubmissionTableRow[]>(() => {
    const groupedAttempts = groupSubmissionsByAttemptKey(courseSubmissions);

    const attemptLookup = Object.values(groupedAttempts).reduce<
      Record<string, { maxAttempts: number; submissionAttempt: number }>
    >((lookup, group) => {
      const sortedGroup = [...group].sort(
        (left, right) => getSubmissionSortTimestamp(left) - getSubmissionSortTimestamp(right)
      );
      const derivedMaxAttempts = Math.max(
        sortedGroup.length,
        sortedGroup.some((submission) => submission.retakeDeadline != null) ? 2 : 1
      );

      sortedGroup.forEach((submission, index) => {
        lookup[submission.id] = {
          maxAttempts: derivedMaxAttempts,
          submissionAttempt: submission.status === "Missing" ? 0 : index + 1,
        };
      });

      return lookup;
    }, {});

    return [...courseSubmissions]
      .map((submission) => {
        const isLate =
          submission.status !== "Missing" &&
          isSubmissionLate({
            submittedDate: submission.submittedOn,
            dueDate: submission.dueDate,
            retakeDeadline: submission.retakeDeadline,
            retakeDeadlineSnapshot: null,
          });
        const scorePercentage = getRoundedPercentage(submission.grade ?? null, submission.maximumPoints ?? null);
        const attemptDetails = attemptLookup[submission.id] ?? {
          maxAttempts: submission.retakeDeadline ? 2 : 1,
          submissionAttempt: submission.status === "Missing" ? 0 : 1,
        };

        return {
          submission,
          normalizedStatus: getSubmissionStatus(submission),
          isLate,
          scorePercentage,
          sortTimestamp: getSubmissionSortTimestamp(submission),
          ...attemptDetails,
        };
      })
      .sort((left, right) => right.sortTimestamp - left.sortTimestamp);
  }, [courseSubmissions]);

  if (!studentInsights) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Failed to load grades and submissions" size="m" weight="medium" useTranslationText={false} />
      </View>
    );
  }

  const totalSubmissions = submissionRows.length;
  const gradedCount = submissionRows.filter(
    (row) => row.normalizedStatus !== "Missing" && row.submission.grade != null
  ).length;
  const missingCount = submissionRows.filter((row) => row.normalizedStatus === "Missing").length;
  const lateSubmissionsCount = submissionRows.filter((row) => row.isLate).length;

  const aggregateGradePct =
    studentInsights.student.aggregateGradePct ?? studentInsights.courseSnapshot?.aggregateGradePct ?? null;
  const completedActivities = studentInsights.courseSnapshot?.completedActivities ?? null;
  const totalActivities = studentInsights.courseSnapshot?.totalActivities ?? null;
  const roundedGradePct = typeof aggregateGradePct === "number" ? Math.round(aggregateGradePct) : null;
  const runningGrade = roundedGradePct != null ? `${roundedGradePct}%` : "—";
  const hasLowGrade = roundedGradePct != null && roundedGradePct < 70;

  const assignmentTotals = sumSubmissionPoints("Assignment", courseSubmissions);
  const assessmentTotals = sumSubmissionPoints("Assessment", courseSubmissions);
  const assignmentPercentage = getRoundedPercentage(assignmentTotals.earned, assignmentTotals.possible);
  const assessmentPercentage = getRoundedPercentage(assessmentTotals.earned, assessmentTotals.possible);
  const participationPercentage =
    totalActivities != null && totalActivities > 0
      ? Math.round(((completedActivities ?? 0) / totalActivities) * 100)
      : null;

  const gradeBreakdownRows: GradeBreakdownRow[] = [
    {
      label: "Assignments",
      percentage: assignmentPercentage,
      earned: assignmentTotals.earned,
      possible: assignmentTotals.possible,
      progressColor: getBreakdownProgressColor(assignmentPercentage),
    },
    {
      label: "Assessments",
      percentage: assessmentPercentage,
      earned: assessmentTotals.earned,
      possible: assessmentTotals.possible,
      progressColor: getBreakdownProgressColor(assessmentPercentage),
    },
    {
      label: "Participation",
      percentage: participationPercentage,
      earned: completedActivities,
      possible: totalActivities,
      progressColor: getBreakdownProgressColor(participationPercentage),
    },
  ];

  const handleViewSubmission = (submissionId: string) => {
    navigation.navigate("Admin Grading Center", { submissionId });
  };

  return (
    <View style={styles.root}>
      <RosterInsightSummaryMetrics
        runningGrade={runningGrade}
        hasLowGrade={hasLowGrade}
        gradedCount={gradedCount}
        totalSubmissions={totalSubmissions}
        missingCount={missingCount}
        lateSubmissionsCount={lateSubmissionsCount}
      />

      <RosterInsightGradeBreakdownCard rows={gradeBreakdownRows} />

      <RosterInsightSubmissionsTable submissionRows={submissionRows} onViewSubmission={handleViewSubmission} />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    marginVertical: spacing.lg,
    gap: spacing.lg,
  },
  errorContainer: {
    minHeight: 180,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
});

export default RosterInsightGradesAndSubmissionsContainer;
